import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LifeBuoy, Plus } from "lucide-react";
import { TICKET_STATUSES, PRIORITIES, statusMeta } from "@/lib/taskman";

export default function PortalTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", priority: "normal" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client-portal/tickets");
      setTickets(data.tickets || []);
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTicket = async () => {
    if (!form.subject.trim()) return;
    setSaving(true);
    try {
      await api.post("/client-portal/tickets", form);
      toast.success("Ticket raised — our team will pick it up shortly");
      setCreateOpen(false);
      setForm({ subject: "", description: "", priority: "normal" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to raise ticket"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-[900px]" data-testid="portal-tickets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-[#002FA7]" /> Support Tickets
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Raise a request for work and track its progress here.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="portal-new-ticket-btn">
          <Plus className="h-4 w-4 mr-1" /> New ticket
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>
      ) : tickets.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-zinc-400"
          data-testid="portal-tickets-empty">
          No tickets yet — raise one when you need something done.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const st = statusMeta(TICKET_STATUSES, t.status);
            const pr = statusMeta(PRIORITIES, t.priority);
            const tc = t.task_counts || { total: 0, done: 0 };
            const pct = tc.total ? Math.round((tc.done / tc.total) * 100) : 0;
            return (
              <Card key={t.ticket_id} data-testid={`portal-ticket-${t.ticket_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-zinc-800">{t.subject}</div>
                      {t.description && (
                        <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.description}</p>
                      )}
                      <div className="text-[11px] text-zinc-400 mt-1">
                        Raised {(t.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${pr.cls}`}>{pr.label}</span>
                      <span className={`inline-flex px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${st.cls}`}>{st.label}</span>
                    </div>
                  </div>
                  {tc.total > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                        <span>{tc.done}/{tc.total} tasks complete</span><span>{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="portal-new-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Raise a ticket</DialogTitle>
            <DialogDescription className="text-xs">Tell us what you need and we'll get onto it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">What do you need?</Label>
              <Input value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="e.g. Update the homepage banner"
                data-testid="portal-ticket-subject" />
            </div>
            <div>
              <Label className="text-xs">Details</Label>
              <Textarea rows={4} value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="portal-ticket-description" />
            </div>
            <div>
              <Label className="text-xs">How urgent is it?</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="portal-ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTicket} disabled={saving || !form.subject.trim()}
              data-testid="portal-ticket-submit">
              {saving ? "Submitting…" : "Raise ticket"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
